import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedNumber } from 'react-intl';
import { To } from 'react-router-dom';
import styled from 'styled-components';

import { Lightning } from 'atoms/icons/Lightning';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { CircularProgress } from 'atoms/loader/CircularProgress';
import { LabelM, LabelS } from 'atoms/typography';
import { Ellipsis } from 'atoms/typography/Ellipsis';
import { LeaderboardRow } from 'components/layout/LeaderboardRow';
import { ClubShield } from 'components/user/ClubShield';
import { type FragmentOf, unmaskFragment } from 'lib/graphql';

import type {
  CollectionLeaderboardRow_PublicUserInfoInterface,
  CollectionLeaderboardRow_UserSportProfileInterface,
} from './__generated__/index.graphql';

const Children = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: end;
`;
const StyledProgress = styled(CircularProgress)`
  width: var(--intermediate-unit);
  --circular-progress-stroke: var(--c-onBase-success);
`;

type Props = {
  to?: To;
  rank?: number | null;
  user: FragmentOf<CollectionLeaderboardRow_PublicUserInfoInterface>;
  userSportProfile?: Nullable<
    FragmentOf<CollectionLeaderboardRow_UserSportProfileInterface>
  >;
  score?: number;
  ownedCards?: number;
  totalCards?: number;
  highlighted?: boolean;
};

export const CollectionLeaderboardRow = ({
  to,
  rank,
  user: user_,
  userSportProfile: userSportProfile_,
  score = 0,
  ownedCards = 0,
  totalCards = 0,
  highlighted,
}: Props) => {
  const userSportProfile = unmaskFragment(userSportProfile_);
  const user = unmaskFragment(user_);
  return (
    <LeaderboardRow
      highlighted={highlighted}
      rank={rank}
      user={user}
      to={to}
      caption={
        <Horizontal gap={0.5}>
          <ClubShield
            size={1.5}
            shieldUrl={userSportProfile?.clubBadge?.pictureUrl}
            clubName={userSportProfile?.clubName}
          />
          <LabelS color="var(--c-nd-600)">
            <Ellipsis>{userSportProfile?.clubName}</Ellipsis>
          </LabelS>
        </Horizontal>
      }
    >
      <Children>
        <Horizontal>
          <LabelM bold>
            <FormattedNumber value={score} />
          </LabelM>
          <Lightning size={1.5} />
        </Horizontal>
        <Horizontal>
          <Horizontal gap={0}>
            <LabelS color="var(--c-nd-600)">
              <b>
                <FormattedNumber value={ownedCards} />
              </b>
              /<FormattedNumber value={totalCards} />
            </LabelS>
          </Horizontal>
          <StyledProgress maxProgress={totalCards} progress={ownedCards} />
        </Horizontal>
      </Children>
    </LeaderboardRow>
  );
};

CollectionLeaderboardRow.fragments = {
  PublicUserInfoInterface: gql`
    fragment CollectionLeaderboardRow_PublicUserInfoInterface on PublicUserInfoInterface {
      slug
      ...LeaderboardRow_publicUserInfoInterface
    }
    ${LeaderboardRow.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<CollectionLeaderboardRow_PublicUserInfoInterface>,
  UserSportProfileInterface: gql`
    fragment CollectionLeaderboardRow_UserSportProfileInterface on UserSportProfileInterface {
      id
      clubName
      clubBadge {
        id
        pictureUrl
      }
    }
  ` as TypedDocumentNode<CollectionLeaderboardRow_UserSportProfileInterface>,
};
